/**
 * Document types aligned with backend API
 *
 * These types exactly match the backend Pydantic models to ensure
 * full compatibility between frontend and backend.
 */

export enum DocumentStatus {
  DRAFT = 'draft',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
  RETIRED = 'retired',
}

export type DocumentResponse = {
  /** Unique identifier for the document (UUID) */
  id: string;
  /** Name of the document */
  name: string;
  /** Business-friendly identifier (e.g., "SOP-0001") */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  display_id: string;
  /** ID of the document creator (UUID) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  created_by: string;
  /** Tenant to which this document belongs (UUID) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  tenant_id: string;
  /** When the document was created (ISO datetime) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  created_at: string;
  /** When the document was last updated (ISO datetime) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  updated_at: string;
  /** Current status of the document (optional) */
  status?: string;
  /** ID of the document type (UUID) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  document_type_id: string;
  /** Prefix of the document type (e.g., "SOP") */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  document_type_prefix: string;
  /** ID of the latest revision (UUID, nullable) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  latest_revision_id: string | undefined;
  /** Number of the latest revision (nullable) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  latest_revision_number: number | undefined;
  /** Label of the latest revision (e.g., "A", "01", nullable) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  latest_revision_label?: string | undefined;
  /** ID of the published revision (UUID, nullable) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  published_revision_id: string | undefined;
  /** Number of the published revision (nullable) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  published_revision_number: number | undefined;
  /** Label of the published revision (e.g., "A", "01", nullable) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  published_revision_label?: string | undefined;
  /** Additional metadata for the document */
  metadata: Record<string, unknown>;
  /** Legacy revisions from pre-MATIC systems */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  legacy_revisions?: LegacyRevisionResponse[] | null;
  /** Whether this document has been processed by ETL text extraction (only included when requested) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  has_extracted_text?: boolean | null;
};

/** Legacy revision from a pre-MATIC system (snake_case API response) */
export type LegacyRevisionResponse = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  revision_label: string;
  changelog: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  system_name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  published_date?: string | null;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  approval_date?: string | null;
};

/** Frontend camelCase legacy revision type */
export type LegacyRevision = {
  revisionLabel: string;
  changelog: string;
  systemName: string;
  publishedDate?: string;
  approvalDate?: string;
};

/**
 * Document list response model matching backend DocumentListResponse
 */
export type DocumentListResponse = {
  /** List of documents */
  items: DocumentResponse[];
  /** Total number of documents matching the query */
  total: number;
  /** Maximum number of documents returned */
  limit: number;
  /** Number of documents skipped */
  offset: number;
};

// ============================================================================
// Admin User Management Pattern Interfaces (Frontend camelCase types)
// ============================================================================

/**
 * Frontend Document interface with camelCase properties
 * Converted from DocumentResponse (snake_case) by API client
 */
export type Document = {
  /** Unique identifier for the document (UUID) */
  id: string;
  /** Name of the document */
  name: string;
  /** Business-friendly identifier (e.g., "SOP-0001") - camelCase */
  displayId: string;
  /** ID of the document creator (UUID) - camelCase */
  createdBy: string;
  /** Tenant to which this document belongs (UUID) - camelCase */
  tenantId: string;
  /** When the document was created (ISO datetime) - camelCase */
  createdAt: string;
  /** When the document was last updated (ISO datetime) - camelCase */
  updatedAt: string;
  /** Current status of the document (optional) - camelCase */
  status?: string;
  /** ID of the document type (UUID) - camelCase */
  documentTypeId: string;
  /** Prefix of the document type (e.g., "SOP") - camelCase */
  documentTypePrefix: string;
  /** ID of the latest revision (UUID, nullable) - camelCase */
  latestRevisionId?: string | undefined;
  /** Number of the latest revision (nullable) - camelCase */
  latestRevisionNumber?: number | undefined;
  /** Label of the latest revision (e.g., "A", "01", nullable) - camelCase */
  latestRevisionLabel?: string | undefined;
  /** ID of the published revision (UUID, nullable) - camelCase */
  publishedRevisionId?: string | undefined;
  /** Number of the published revision (nullable) - camelCase */
  publishedRevisionNumber?: number | undefined;
  /** Label of the published revision (e.g., "A", "01", nullable) - camelCase */
  publishedRevisionLabel?: string | undefined;
  /** Additional metadata for the document */
  metadata?: Record<string, unknown>;
  /** Legacy revisions from pre-MATIC systems */
  legacyRevisions?: LegacyRevision[];
  /** Optional flag to identify synthetic "published-only" entries */
  isPublishedOnly?: boolean;
  /** Whether this document has been processed by ETL text extraction */
  hasExtractedText?: boolean;
};

/**
 * Frontend DocumentList interface with camelCase Document items
 */
export type DocumentList = {
  /** List of documents (camelCase) */
  items: Document[];
  /** Total number of documents matching the query */
  total: number;
  /** Maximum number of documents returned */
  limit: number;
  /** Number of documents skipped */
  offset: number;
};

/**
 * Document filter options for querying documents
 */
export type DocumentFilters = {
  /** Filter by document status */
  status?: string;
  /** Filter by document type */
  documentType?: string;
  /** Search query string */
  search?: string;
  /** Maximum number of results to return */
  limit?: number;
  /** Number of results to skip */
  offset?: number;
};
